import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import type { BusinessProcessDetail } from './types'

const EVENT_LABELS: Record<string, string> = {
  created: 'Criado',
  draft_updated: 'Rascunho atualizado',
  draft_approved: 'Rascunho aprovado',
  sent_to_agent: 'Documento solicitado',
  procedure_document_request_failed: 'Falha ao solicitar documento',
  agent_cancelled: 'Envio cancelado',
  agent_comment_sent: 'Comentário enviado',
  procedure_document_imported: 'Documento importado',
  procedure_document_import_failed: 'Falha no documento',
  procedure_document_approved: 'Documento aprovado',
  skill_generation_requested: 'Preparação solicitada',
  skill_generation_request_failed: 'Falha ao solicitar preparação',
  skill_imported: 'Instruções importadas',
  skill_import_failed: 'Falha na preparação',
  published: 'Disponível ao agente',
  publication_failed: 'Falha ao disponibilizar',
  archived: 'Arquivado',
}

function formatDate(value: string | null) {
  if (!value) return 'Ainda não'
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

// Saved attachments and diagnostic trail rendered only for StationZero staff
// (the caller already gates on can_view_technical_details).
export default function ProcessAdminSections({ process }: { process: BusinessProcessDetail }) {
  const canRepublishVersion = process.can_manage && ['skill_generated', 'publication_failed', 'published'].includes(process.internal_status || '')

  return (
    <>
      <section className="rounded-md border p-4">
        <h2 className="text-sm font-semibold">Anexos atuais</h2>
        <div className="mt-3 space-y-2">
          {process.attachments.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum anexo.</p>
          ) : process.attachments.map((attachment) => (
            <a key={attachment.id} href={attachment.url} className="block truncate text-sm text-primary hover:underline">
              {attachment.filename}
            </a>
          ))}
        </div>
      </section>

      <section className="rounded-md border p-4">
        <h2 className="text-sm font-semibold">Histórico</h2>
        <div className="mt-3 space-y-3">
          {process.events.map((event) => (
            <div key={event.id} className="text-sm">
              <div className="font-medium">{EVENT_LABELS[event.action] || event.action}</div>
              <div className="text-xs text-muted-foreground">
                {formatDate(event.created_at)}{event.version_number ? ` · v${event.version_number}` : ''}{event.user_name ? ` · ${event.user_name}` : ''}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-md border p-4">
        <h2 className="text-sm font-semibold">Versões</h2>
        <div className="mt-3 space-y-3">
          {process.versions.map((version) => (
            <div key={version.id} className="border-b pb-3 last:border-b-0 last:pb-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">Versão {version.version_number}</span>
                {version.id === process.published_version_id && <span className="text-xs text-emerald-600">disponível ao agente</span>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{formatDate(version.created_at)}</p>
              {canRepublishVersion && version.generated && version.id !== process.published_version_id && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => router.post(`/dashboard/business_processes/${process.id}/publish`, { version_id: version.id }, { preserveScroll: true })}
                >
                  Disponibilizar versão ao agente
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-md border p-4">
        <h2 className="text-sm font-semibold">Disponibilizações ao agente</h2>
        <div className="mt-3 space-y-3">
          {process.publications.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma disponibilização registrada.</p>
          ) : process.publications.map((publication) => (
            <div key={publication.id} className="text-sm">
              <div className="font-medium">Versão {publication.version_number} · {publication.status === 'succeeded' ? 'sucesso' : 'falha'}</div>
              <div className="text-xs text-muted-foreground">{formatDate(publication.created_at)}</div>
              {publication.error_message && <div className="mt-1 text-xs text-destructive">{publication.error_message}</div>}
            </div>
          ))}
        </div>
      </section>
    </>
  )
}
